import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ApplicationFilters {
  jobId?: string;
  companyId?: string;
  status?: string[];
  priority?: string[];
  assignedTo?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function fetchEmployerApplications(filters: ApplicationFilters = {}) {
  // First, get job IDs for the company if companyId is provided
  let jobIds: string[] | undefined;

  if (filters.companyId) {
    const { data: jobs, error: jobsError } = await supabase
      .from("jobs")
      .select("id")
      .eq("company_id", filters.companyId);

    if (jobsError) throw jobsError;
    jobIds = jobs?.map(j => j.id) || [];

    // If no jobs found for this company, return empty array
    if (jobIds.length === 0) {
      return [];
    }
  }

  let query = supabase
    .from("job_applications")
    .select(`
      *,
      job:jobs(
        id,
        title,
        company_name,
        company_logo_url
      ),
      profile:profiles!job_applications_job_seeker_id_fkey(
        id,
        full_name,
        email,
        phone,
        avatar_url,
        professional_title,
        location
      ),
      timeline:application_timeline(count),
      notes:application_notes(count),
      feedback:application_feedback(count)
    `)
    .order("applied_at", { ascending: false });

  // Filter by company's jobs
  if (jobIds && jobIds.length > 0) {
    query = query.in("job_id", jobIds);
  }

  // Filter by specific job
  if (filters.jobId) {
    query = query.eq("job_id", filters.jobId);
  }

  // Filter by status
  if (filters.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }

  // Filter by priority
  if (filters.priority && filters.priority.length > 0) {
    query = query.in("priority", filters.priority);
  }

  // Filter by assigned to
  if (filters.assignedTo) {
    query = query.eq("assigned_to", filters.assignedTo);
  }

  // Date range filter
  if (filters.dateFrom) {
    query = query.gte("applied_at", filters.dateFrom);
  }
  if (filters.dateTo) {
    query = query.lte("applied_at", filters.dateTo);
  }

  const { data, error } = await query;

  if (error) throw error;

  // Client-side search filter (name or email)
  let filteredData = data || [];
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredData = filteredData.filter(
      (app: any) =>
        app.profile?.full_name?.toLowerCase().includes(searchLower) ||
        app.profile?.email?.toLowerCase().includes(searchLower)
    );
  }

  // Ensure pipeline_stage defaults to 'new' if not set
  return filteredData.map((app: any) => ({
    ...app,
    pipeline_stage: app.pipeline_stage || "new",
    profiles: app.profile,
    jobs: app.job,
  }));
}

export function useEmployerApplications(filters: ApplicationFilters = {}) {
  return useQuery({
    queryKey: ["employer-applications", filters],
    queryFn: () => fetchEmployerApplications(filters),
  });
}
